import React, { useEffect, useState } from 'react';

import { Flex, Image, Link } from '@chakra-ui/react';
import FileUpload from 'components/FileUpload/FileUpload';
import { useToast } from 'components/hooks/useToast';

import { Cross, Download, File as FileIcon, Refresh } from 'assets/assetsV1/iconComponents';
import uploadFile from 'assets/assetsV1/illustrations/uploadFile.svg';
import uploadFileComplete from 'assets/assetsV1/illustrations/uploadFileComplete.svg';
import uploadFileFail from 'assets/assetsV1/illustrations/uploadFileFail.svg';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextHeader2 } from 'uiToolkitV1/Typography';

const UPLOAD_STAGES = {
  START: 'START',
  UPLOADING: 'UPLOADING',
  FINISHED: 'FINISHED',
};

const UploadStart = ({
  setUploadStage,
  setSelectedFile,
  heading,
  subHeading,
  sampleFileURI,
  uploadInstructionComp,
}) => {
  const toast = useToast();
  // validate file size to be less than 10MB
  const setSelectedFileWithValidation = (file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File size is too large',
        description: 'Please upload a file less than 10MB',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setSelectedFile(file);
    setUploadStage(UPLOAD_STAGES.UPLOADING);
  };

  return (
    <Flex flexDir="column" alignItems="center" w="100%" flexGrow={1}>
      <Flex flexDir="column" alignItems="center" flexGrow={1} justifyContent="center">
        <Image src={uploadFile} maxH="100px" />
        <TextHeader2 bold mt={5}>
          {heading}
        </TextHeader2>
        <TextBodyMedium color="text.g3" mb={4}>
          {subHeading}
        </TextBodyMedium>
        {/* <Button StartIcon={ArrowCircleUp}>Upload CSV File</Button> */}
        <FileUpload buttonText="Upload CSV File" setFile={setSelectedFileWithValidation} accept=".csv" />

        <Link href={sampleFileURI} download="CSVSample.csv" mt={4}>
          <Button StartIcon={Download} variant="tertiary">
            Download Sample Template
          </Button>
        </Link>
      </Flex>
      {uploadInstructionComp}
    </Flex>
  );
};

const Uploading = ({
  selectedFile,
  uploadFileHandler,

  discardFile,
  dataSetter,
  postUploadDataInfoComp,
  retryUseQueryInvalidator,
}) => {
  const { isLoading, data, isSuccess, isError } = uploadFileHandler({
    fileObject: selectedFile,
  });

  useEffect(() => {
    if (!data) return;
    dataSetter(data);
  }, [data]);

  return (
    <Flex flexDir="column" p="0 8px" alignItems="center" flexGrow={1} justifyContent="center">
      {isSuccess && <Image src={uploadFileComplete} maxH="200px" mb={5} />}
      {!isSuccess && !isError && <Image src={uploadFile} maxH="200px" mb={5} />}
      {isError && <Image src={uploadFileFail} maxH="200px" mb={5} />}
      <TextHeader2 bold mt={4}>
        {isError ? 'Upload Failed' : isSuccess ? 'Uploaded Successfully' : 'Uploading...'}
      </TextHeader2>

      {isLoading && <TextBodyMedium color="text.g3">This might take few minutes to reflect.</TextBodyMedium>}
      {isError && <TextBodyMedium color="text.g3">Uh-oh! This shouldn’t have happened.</TextBodyMedium>}
      <Flex mt={6}>
        <FileIcon boxSize="20px" color="icon.g2" />
        <TextBodyMedium mx={2}>{selectedFile.name}</TextBodyMedium>
        <Cross boxSize="20px" color="icon.g2" onClick={discardFile} cursor="pointer" />
      </Flex>

      {isError && (
        <Button
          StartIcon={Refresh}
          variant="tertiary"
          mt={3}
          // trick to cause retry
          onClick={retryUseQueryInvalidator}
        >
          Try Again
        </Button>
      )}
      {postUploadDataInfoComp}
    </Flex>
  );
};

export const UploadFileComp = ({
  heading,
  subHeading,
  sampleFileURI,
  dataSetter,
  postUploadDataInfoComp,
  uploadFileHandler,
  onSelectFile,
  uploadInstructionComp,
  retryUseQueryInvalidator = () => {},
}) => {
  const [uploadStage, setUploadStage] = useState(UPLOAD_STAGES.START);
  const [selectedFile, setSelectedFile] = useState(null);
  const discardFile = () => {
    setSelectedFile(null);
    setUploadStage(UPLOAD_STAGES.START);
  };

  const setSelectedFileHandler = (file) => {
    setSelectedFile(file);
    onSelectFile(file);
  };

  return (
    <Flex p="16px 8px" flexGrow={1} alignItems="center" flexDir="column">
      {uploadStage === UPLOAD_STAGES.START && (
        <UploadStart
          setUploadStage={setUploadStage}
          setSelectedFile={setSelectedFileHandler}
          heading={heading}
          subHeading={subHeading}
          sampleFileURI={sampleFileURI}
          uploadInstructionComp={uploadInstructionComp}
        />
      )}
      {[UPLOAD_STAGES.UPLOADING, UPLOAD_STAGES.FINISHED].includes(uploadStage) && (
        <Uploading
          selectedFile={selectedFile}
          discardFile={discardFile}
          dataSetter={dataSetter}
          postUploadDataInfoComp={postUploadDataInfoComp}
          uploadFileHandler={uploadFileHandler}
          retryUseQueryInvalidator={retryUseQueryInvalidator}
        />
      )}
    </Flex>
  );
};
